import {
  Applicant,
  ApplicantRequest,
  ApplicationDetailLog,
  ApplicationLogsRequest,
  ApplicantAddress
} from 'leia-api';

export class ApplicantView extends Applicant {
  address?: ApplicantAddress;
}

export class Application extends ApplicationDetailLog {
  uniqueLink?: string;
  uniqueCode?: string;
  applicants?: Array<ApplicantView>;
  applicationChannelDescription?: string;

  /**
   * Map an application detail log to an Application object
   * @param log Log to map
   */
  static mapFromApplicantLog(log: ApplicationDetailLog): Application {
    let application = new Application();
    application.applicants = log.applicants ?? new Array<Applicant>();
    application.sessionId = log.sessionId;
    application.utmCampaign = log.utmCampaign;
    application.url = log.url;
    application.hasConsentDisclosure = log.hasConsentDisclosure;
    application.hasConsentTermsCond = log.hasConsentTermsCond;
    application.fund = log.fund;
    application.fundedAmount = log.fundedAmount;
    application.steps = log.steps;
    application.typeOfIdDocuments = log.typeOfIdDocuments;
    application.createdTimestamp = log.createdTimestamp;
    application.updatedTimestamp = log.updatedTimestamp;
    application.lastCompletedStep = log.lastCompletedStep;
    application.lastCompletedStepState = log.lastCompletedStepState;
    application.status = log.status;
    application.hasIRSVerification = log.hasIRSVerification;
    application.applicationChannelId = log.applicationChannelId;
    application.branchId = log.branchId === null ? "0" : log.branchId;
    application.applicationChannelDescription = application.setChannelName(log);
    application.isJoint = log.isJoint;
    application.applicationType = log.applicationType;
    application.parentApplicationId = log.parentApplicationId;
    application.rcCode = log.rcCode;
    application.linkedAccountNumber = log.linkedAccountNumber;
    application.promoCodes = log.promoCodes;
    application.costCenter = log.costCenter;
    application.officerCode = log.officerCode;
    application.journeyToken = log.journeyToken;
    application.products = log.products;
    return application;
  }

  /**
   * Map state for display
   */
  public mapStateForDisplay() {
    switch (this.status?.toUpperCase()) {
      case "STARTED":
        return {"displayText": "Started", "classes": "badge bg-primary"};
      case "CREATED":
        return {"displayText": "Created", "classes": "badge bg-created"};
      case "CANCELED":
        return {"displayText": "Canceled", "classes": "badge bg-secondary"};
      case "ABANDONED":
        return {"displayText": "Abandoned", "classes": "badge bg-abandoned"};
      case "REJECTED":
        return {"displayText": "Rejected", "classes": "badge bg-danger"};
      case "APPROVED":
        return {"displayText": "Approved", "classes": "badge bg-success"};
      case "UNDER REVIEW":
        return {"displayText": "Under Review", "classes": "badge bg-under-review"};
      case "REVIEW CLOSED":
        return {"displayText": "Review Closed", "classes": "badge bg-secondary"};
      case "EXPIRED":
        return {"displayText": "Expired", "classes": "badge bg-secondary"};
      case "ERROR":
        return {"displayText": "Error", "classes": "badge bg-danger"};
      case "PENDING":
        return {"displayText": "Pending", "classes": "badge bg-created"};
      default:
        return {};
    }
  }

  /**
   * Map state for display
   */
  public applicationFutureState() {
    switch (this.status?.toUpperCase()) {
      case "STARTED":
        return ["Under Review", "Rejected", "Approved"];
      case "ABANDONED":
        return ["Under Review", "Rejected", "Approved"];
      case "REJECTED":
        return ["Under Review", "Approved"];
      case "UNDER REVIEW":
        return ["Rejected", "Approved"];
      default:
        return [];
    }
  }

  static mapApplicationLogRequest(application: Application): ApplicationLogsRequest {
    let applicationLogRequest = <ApplicationLogsRequest>{};
    applicationLogRequest.sessionId = application.sessionId!;
    applicationLogRequest.utmCampaign = application.utmCampaign;
    applicationLogRequest.status = application.status! as ApplicationLogsRequest.StatusEnum;
    applicationLogRequest.hasConsentDisclosure = application.hasConsentDisclosure;
    applicationLogRequest.hasConsentTermsCond = application.hasConsentTermsCond;
    applicationLogRequest.fund = application.fund;
    applicationLogRequest.fundedAmount = application.fundedAmount;
    applicationLogRequest.typeOfIdDocuments = application.typeOfIdDocuments;
    applicationLogRequest.products = application.products;
    applicationLogRequest.productUIDList = application.products?.map(product => product.productUID).join(",");
    applicationLogRequest.hasIRSVerification = application.hasIRSVerification;
    applicationLogRequest.applicationChannelId = application.applicationChannelId;
    applicationLogRequest.branchId = application.branchId ?? "0";
    applicationLogRequest.isJoint = application.isJoint;
    applicationLogRequest.applicationType = application.applicationType;
    applicationLogRequest.parentApplicationId = application.parentApplicationId;
    applicationLogRequest.rcCode = application.rcCode;
    applicationLogRequest.promoCodes = application.promoCodes;
    applicationLogRequest.costCenter = application.costCenter;
    applicationLogRequest.officerCode = application.officerCode;
    applicationLogRequest.journeyToken = application.journeyToken;
    applicationLogRequest.url = application.url;

    let applicationRequestList = Array<ApplicantRequest>();
    application.applicants!.forEach(applicant => {
      let applicantRequest = new ApplicantRequest();
      applicantRequest.applicantId = applicant.applicantId;
      applicantRequest.cif = applicant.cif;
      applicantRequest.primary = applicant.primary;
      applicantRequest.annualIncome = applicant.annualIncome;
      applicantRequest.dateOfBirth = applicant.dateOfBirth;
      applicantRequest.email = applicant.email;
      applicantRequest.employerCountry = applicant.employerCountry;
      applicantRequest.firstName = applicant.firstName;
      applicantRequest.isEnrolledOLB = applicant.isEnrolledOLB;
      applicantRequest.isInGoodStanding = applicant.isInGoodStanding;
      applicantRequest.isExistingCustomer = applicant.isExistingCustomer;
      applicantRequest.isKyc = applicant.isKyc;
      applicantRequest.isPasswordValid = applicant.isPasswordValid;
      applicantRequest.middleName = applicant.middleName;
      applicantRequest.lastName = applicant.lastName;
      applicantRequest.suffix = applicant.suffix;
      applicantRequest.occupation = applicant.occupation;
      applicantRequest.phoneNumber = applicant.phoneNumber;
      applicantRequest.primarySourceOfFunds = applicant.primarySourceOfFunds;
      applicantRequest.returningUser = applicant.returningUser;
      applicantRequest.ssn = applicant.ssn;
      applicantRequest.userName = applicant.userName;
      applicantRequest.addresses = applicant.addresses;
      applicationRequestList.push(applicantRequest);
    });
    applicationLogRequest.applicants = applicationRequestList;
    return applicationLogRequest;
  }

  static applicationChannelMap(): Map<number, string> {
    let applicationChannelMap = new Map<number, string>();

    applicationChannelMap.set(0, 'Unknown');
    applicationChannelMap.set(1, 'Digital Self-Serve');
    applicationChannelMap.set(2, 'Banking Center');
    applicationChannelMap.set(3, 'FSR/RM Assisted');
    applicationChannelMap.set(4, 'OLB - Joint Request');
    applicationChannelMap.set(5, 'CSR - Joint Request');
    applicationChannelMap.set(6, 'OLB - Authenticated');
    return applicationChannelMap;
  }

  private setChannelName(applicationDetailLog: ApplicationDetailLog) {
    let channelName = "Unknown";
    if (!!applicationDetailLog.applicationChannelId) {
      channelName = Application.applicationChannelMap().get(applicationDetailLog.applicationChannelId)!;
    }
    return channelName;
  }
}


/**
 * Log class
 */
export class Log {
  constructor(
    public source?: string,
    public timestamp?: Date,
    public details?: string
  ) {
  }

  public classes?: string = "";
}
